import { useCallback, useMemo, useState } from "react";
import { useDispatch, useSelector, batch } from "react-redux";
import { getAccountState } from "../../../../redux/slices/accountSlice";
import { API } from "../../../../API";
import AlertContainer from "../../../Containers/AlertContainer";
import { Icon } from "@iconify/react";
import { Colors } from "../../../../utils/colors";
import IntegrationPanelContainer from "./IntegrationPanelContainer";
import {
  GetTime,
  hasSubscriptionUnlockedAdvancedData,
} from "@markit/common.utils";
import { GetNumericDate } from "../../../../utils/GetTime";
import { CircularProgress } from "@mui/material";
import { HorizontalDivider } from "../../../Dividers/HorizontalDivider";
import { showNotificationBanner } from "../../../../utils/notificationUtils";
import { PlanTypeV4, FollowType, NotificationType } from "@markit/common.types";
import { notificationActions } from "../../../../redux/slices/notificationSlice";
import ShowclixLogo from "../../../../assets/ShowclixLogo.png";
import {
  eventActions,
  fetchCreatorEvents,
  getEventState,
  refreshEventData,
} from "../../../../redux/slices/eventSlice";
import { fetchCurrUserWishList } from "../../../../redux/slices/wishMarkSlice";
import useAsyncOnMount from "../../../../hooks/useAsyncEffectOnMount";
import { fetchNumFollowTypeFollowers } from "../../../../utils/userUtils";
import { checkLastIntegrationSyncDate } from "../../../../utils/eventbriteUtils";
import HelpModal from "../../../Containers/HelpModal";

type ShowclixPanelSectionProps = {};

export const ShowclixPanelSection = (props: ShowclixPanelSectionProps) => {
  const { accountData } = useSelector(getAccountState).account;
  const { events, backgroundShowclixEventsSync } =
    useSelector(getEventState).events;
  const dispatch = useDispatch();
  const [numShowclixContacts, setNumShowclixContacts] = useState(-1);
  const [loading, setLoading] = useState(backgroundShowclixEventsSync);
  const [helpModal, setHelpModal] = useState(false);
  const [alertText, setAlertText] = useState<{
    header: string;
    subHeader: string;
  }>({
    header: "",
    subHeader: "",
  });

  const showClixDescription =
    "We work directly with Showclix to provide the #1 integration on the market. Seamlessly connect and sync contacts.";

  useAsyncOnMount(async () => {
    // Get total showclix contacts
    const numShowclixContacts = await fetchNumFollowTypeFollowers(
      accountData.uid,
      FollowType.SHOWCLIX
    );
    setNumShowclixContacts(numShowclixContacts);
  });

  // So that admins can test this without being blocked by no subscription
  const hasUnlockedAdvancedDataOrAdmin = useMemo(
    () =>
      hasSubscriptionUnlockedAdvancedData(accountData) || accountData.isAdmin,
    [accountData]
  );

  const authorizeShowclixOnPress = useCallback(async () => {
    if (!hasUnlockedAdvancedDataOrAdmin) {
      dispatch(notificationActions.setMarkitPlusModal(PlanTypeV4.PRO.name));
      return;
    }
    setHelpModal(true);
    // setLoading(true);
    // await API.eventScraper.authorizeShowclixUser({
    //   userId: accountData.uid,
    // });
    // setLoading(false);
  }, [dispatch, hasUnlockedAdvancedDataOrAdmin]);

  const connectShowclixOnPress = useCallback(async () => {
    setLoading(true);
    const response = await API.eventScraper.connectShowclixData({
      showclixInfo: accountData.showclixInfo,
      userId: accountData.uid,
    });
    batch(() => {
      dispatch(fetchCreatorEvents(accountData.uid));
      dispatch(fetchCurrUserWishList(accountData.uid));
    });
    setLoading(false);
    if (response.success) {
      showNotificationBanner(
        dispatch,
        "Connected!",
        NotificationType.AFFIRMATIVE
      );
    }
  }, [accountData.showclixInfo, accountData.uid, dispatch]);

  const syncShowclixOnPress = useCallback(async () => {
    const disableSync = checkLastIntegrationSyncDate(
      accountData.showclixInfo.syncedOn,
      accountData.isAdmin,
      30
    );
    if (disableSync) {
      setAlertText({
        header: "Cannot sync data at this time.",
        subHeader:
          "Please wait until 30 minutes have passed before syncing again.",
      });
      return;
    }
    setLoading(true);
    dispatch(eventActions.setBackgroundShowclixEventsSync(true));
    // Start the sync process
    API.eventScraper
      .syncShowclixEventsData({
        showclixInfo: accountData.showclixInfo,
        userId: accountData.uid,
      })
      .then(async (response) => {
        if (response.success) {
          await Promise.all(
            events.map((event) => dispatch(refreshEventData(event.id)))
          );
          showNotificationBanner(
            dispatch,
            "Synced!",
            NotificationType.AFFIRMATIVE
          );
        }
        setLoading(false);
        dispatch(eventActions.setBackgroundShowclixEventsSync(false));
      })
      .catch(() => {
        setLoading(false);
        dispatch(eventActions.setBackgroundShowclixEventsSync(false));
      });
  }, [
    accountData.showclixInfo,
    accountData.isAdmin,
    accountData.uid,
    dispatch,
    events,
  ]);

  const connectOnPress = accountData.showclixInfo.token
    ? connectShowclixOnPress
    : authorizeShowclixOnPress;

  const connectLabel = hasUnlockedAdvancedDataOrAdmin
    ? accountData.showclixInfo.token
      ? "Connect"
      : "Contact Support"
    : "Upgrade";

  return (
    <>
      <IntegrationPanelContainer
        isConnected={!!accountData.showclixInfo.connectedOn}
        connectedContent={
          <div
            className="ColumnNormal"
            style={{ height: "100%", width: "100%" }}
          >
            <div className="AlignedRow" style={{ gap: 14, padding: 14 }}>
              <img
                alt="ShowclixLogoSmall"
                src={ShowclixLogo}
                width={60}
                height={60}
              />
              <div className="ColumnNormal" style={{ gap: 7 }}>
                <div className="AlignedRow" style={{ gap: 10 }}>
                  <span className="sectionTitle">Showclix</span>
                  <span className="bodySubtext">Connected</span>
                </div>
                <span className="smallBodySubtext" style={{ width: "80%" }}>
                  {showClixDescription}
                </span>
              </div>
            </div>
            <HorizontalDivider />
            <div
              className="ColumnNormal"
              style={{
                padding: 14,
                flexGrow: 1,
                justifyContent: "space-between",
              }}
            >
              <div className="ColumnNormal" style={{ gap: 10 }}>
                {numShowclixContacts !== -1 ? (
                  <span
                    className="smallBodySubtext"
                    style={{
                      color:
                        numShowclixContacts > 0 ? Colors.GRAY1 : Colors.RED3,
                    }}
                  >
                    Total Valid Synced Contacts: {numShowclixContacts}
                  </span>
                ) : null}
                <span className="smallBodySubtext">
                  Connected On:{" "}
                  {GetNumericDate(accountData.showclixInfo.connectedOn)}
                </span>
              </div>
              <div
                className="AlignedRow"
                style={{ alignContent: "flex-end", gap: 14 }}
              >
                <div className="AlignedRow" style={{ gap: 10 }}>
                  <div
                    className="AlignedRowSelect"
                    style={{ gap: 10, pointerEvents: loading ? "none" : "all" }}
                    onClick={syncShowclixOnPress}
                  >
                    {loading ? (
                      <CircularProgress
                        style={{
                          color: Colors.BLACK,
                          alignSelf: "center",
                          paddingInline: 2,
                        }}
                        size={20}
                      />
                    ) : (
                      <Icon icon="ion:sync-sharp" height={24} />
                    )}
                    <div className="ColumnNormalSelect">
                      <span style={{ fontSize: 12, fontWeight: 500 }}>
                        {loading
                          ? "Syncing in Progress..."
                          : "Sync All Event Data"}
                      </span>
                      {accountData.showclixInfo.syncedOn ? (
                        <span style={{ fontSize: 11, color: Colors.GRAY1 }}>
                          Last Synced:{" "}
                          {GetNumericDate(accountData.showclixInfo.syncedOn)}{" "}
                          {GetTime(accountData.showclixInfo.syncedOn)}
                        </span>
                      ) : null}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        }
        title="Showclix Integration"
        description={showClixDescription}
        actionLabel={connectLabel}
        onActionPress={
          hasUnlockedAdvancedDataOrAdmin
            ? connectOnPress
            : authorizeShowclixOnPress
        }
        actionLoading={loading}
        icon={
          <img
            alt="ShowclixLogo"
            src={ShowclixLogo}
            width={131}
            height={131}
            style={{ marginRight: -18 }}
          />
        }
      />
      <AlertContainer
        headerComp={alertText.header}
        subHeaderComp={alertText.subHeader}
        closeModal={() => setAlertText({ header: "", subHeader: "" })}
        hideModal={alertText.header === ""}
      />
      {helpModal ? (
        <HelpModal showModal={helpModal} setShowModal={setHelpModal} />
      ) : null}
    </>
  );
};
